/**
 * CSV generation utilities
 * RFC 4180 compliant CSV generation for redeem codes
 */

import { appConfig } from '../env';
import type { RedeemCodeApiFormat } from '../types/redeemGroup';
import { csvCell } from './csvField';

/**
 * Generate and download CSV file for redeem codes
 * Creates RFC 4180 compliant CSV with CODE, FULL_URL, and GENERATED_AT columns
 * Triggers browser download with timestamped filename
 *
 * @param codes - Array of redeem code objects to include in CSV
 * @param groupName - Name of the redeem group (used in filename)
 */
export function generateCSV(
  codes: RedeemCodeApiFormat[],
  groupName: string
): void {
  // Generate CSV header and rows. Every cell, header included, goes through
  // the shared cell rule in ./csvField, which mirrors the backend's
  // functions/src/utils/csvHelpers.ts (#2194).
  const header = ['CODE', 'FULL_URL', 'GENERATED_AT'].map(csvCell).join(',');
  const rows = codes.map(codeObj => {
    const fullUrl = `${appConfig.app.url}/redeem/${codeObj.code}`;
    return [codeObj.code, fullUrl, codeObj.generated_at].map(csvCell).join(',');
  });

  // Combine header and rows with CRLF line endings (RFC 4180)
  const csvContent = [header, ...rows].join('\r\n');

  // Create Blob with proper CSV MIME type
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Generate timestamped filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const sanitizedGroupName = groupName.replace(/[^a-zA-Z0-9-_]/g, '_');
  const filename = `redeem-codes-${sanitizedGroupName}-${timestamp}.csv`;

  // Create temporary download link and trigger download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup DOM immediately (link no longer needed after click)
  document.body.removeChild(link);

  // Delay URL revocation to ensure download completes
  // 60 seconds is more than sufficient for download to start
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
}
